import { useState, FormEvent } from 'react';

const LOGO = 'https://static.readdy.ai/image/705f25322c737a89d377410a2840897b/562dc63b58250b7932862d714c3260fa.png';
const PROPNEX_LOGO = 'https://storage.readdy-site.link/project_files/794dc72f-0fe0-459a-adfa-e9a96db79dda/a52aa340-e324-46bd-8a2d-9d1503d0419e_ERA-LOGO.png?v=d33fe71e06a6ab1423752370369ef208';
const FORM_URL = 'https://readdy.ai/api/form/d7165ak991ove783spc0';

const NAV_LINKS = [
  { label: 'Project Details', id: 'project-details' },
  { label: 'Location Map', id: 'location-map' },
  { label: 'Gallery', id: 'gallery' },
  { label: 'Enquiry Form', id: 'enquiry-form' },
];

const DATES: string[] = Array.from({ length: 14 }, (_, i) => {
  const d = new Date();
  d.setDate(d.getDate() + i + 1);
  return d.toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
});

const TIMES = ['10:00 AM', '11:00 AM', '12:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM'];

export default function Footer() {
  const [interests, setInterests] = useState({ brochure: false, pricelist: false, showflat: false });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [dateOpen, setDateOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!email.trim()) { setError('Please enter your email.'); return; }
    if (!phone.trim()) { setError('Please enter your phone number.'); return; }
    if (!privacy) { setError('Please agree to the Privacy Policy.'); return; }

    setSubmitting(true);
    try {
      const selectedInterests = [
        interests.brochure && 'Brochure (WhatsApp)',
        interests.pricelist && 'Price List',
        interests.showflat && 'Showflat Appointment',
      ].filter(Boolean).join(', ') || 'General Enquiry';

      const body = new URLSearchParams({
        interests: selectedInterests,
        name: name.trim(),
        email: email.trim(),
        phone: `+65${phone.trim()}`,
        'appointment-date': date,
        'appointment-time': time,
        privacy: 'agreed',
      });
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSuccess(true);
    } catch {
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer id="contact" className="bg-[#172C37]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex flex-col gap-10 md:flex-row md:gap-16">
          {/* Left Column */}
          <div className="flex-1 flex flex-col gap-6 md:gap-8">
            {/* Logo */}
            <a href="/" className="inline-block">
              <img src={LOGO} alt="Vela Bay Bayshore Logo" className="h-12 w-auto object-contain mix-blend-plus-lighter" />
            </a>

            {/* Agent Info */}
            <div className="space-y-2">
              <p className="text-white font-bold text-base">Appointed Sales Team</p>
              <div className="space-y-1 text-sm text-white/70">
                <div className="flex gap-2">
                  <span className="text-white/40">Agent Name:</span>
                  <span className="text-white">Susie Sun</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-white/40">CEA Reg No:</span>
                  <span className="text-white">R016115F</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-white/40">Agency:</span>
                  <span className="text-white">ERA Realty Pte Ltd</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-white/40">Agency License:</span>
                  <span className="text-white">L3002382K</span>
                </div>
              </div>
              <div className="pt-2">
                <img
                  src={PROPNEX_LOGO}
                  alt="ERA Realty"
                  className="h-8 w-auto object-contain"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Nav Links */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="text-sm text-white/70 hover:text-white transition-colors cursor-pointer text-left py-1"
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* Description */}
            <p className="text-white/50 text-sm leading-relaxed max-w-sm">
              We offer a smarter way to search for property, featuring up-to-date information, full
              photo galleries, floor plans, and key project details. Working directly with the
              official sales team ensures every listing is accurate and genuinely priced.
            </p>

            {/* Phone */}
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Contact Us</p>
              <a href="tel:+6593651286" className="text-xl font-bold text-white hover:text-white/70 transition-colors">
                +65 9365 1286
              </a>
            </div>
          </div>

          {/* Right Column: Secure Your Spot Form */}
          <div className="flex-1">
            <div className="space-y-2 mb-6 md:mb-7">
              <h3
                className="text-3xl md:text-5xl font-bold text-white"
                style={{ fontFamily: '"Cormorant Garamond", serif' }}
              >
                Secure Your Spot!
              </h3>
              <p className="text-white/60 text-sm">
                Register now for exclusive updates on this upcoming project.
              </p>
            </div>

            {success ? (
              <div className="space-y-3 text-center py-8">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto">
                  <i className="ri-check-line text-2xl text-white" />
                </div>
                <p className="text-white font-bold text-lg">Thank you for registering!</p>
                <p className="text-white/60 text-sm">Our team will be in touch within 24 hours.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4" noValidate>
                {/* Interest Checkboxes */}
                <div>
                  <p className="text-white text-sm font-semibold mb-3">I am interested in:</p>
                  <div className="flex flex-col gap-2">
                    {[
                      { key: 'brochure', label: 'Brochure (A copy will be sent to your WhatsApp)' },
                      { key: 'pricelist', label: 'Price List' },
                      { key: 'showflat', label: 'Showflat Appointment' },
                    ].map((opt) => (
                      <label key={opt.key} className="flex items-center gap-3 cursor-pointer group">
                        <div
                          onClick={() => setInterests((prev) => ({ ...prev, [opt.key]: !prev[opt.key as keyof typeof interests] }))}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            interests[opt.key as keyof typeof interests]
                              ? 'bg-white border-white'
                              : 'border-white/40 group-hover:border-white/70'
                          }`}
                        >
                          {interests[opt.key as keyof typeof interests] && (
                            <i className="ri-check-line text-[#172C37] text-xs" />
                          )}
                        </div>
                        <input type="hidden" name={`interest-${opt.key}`} value={interests[opt.key as keyof typeof interests] ? opt.label : ''} />
                        <span className="text-sm text-white/80 group-hover:text-white transition-colors">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Name + Email */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <input
                    name="name"
                    placeholder="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="border border-white/25 bg-transparent text-white placeholder-white/40 rounded-full px-5 py-2.5 text-sm focus:outline-none focus:border-white/60 w-full"
                  />
                  <input
                    name="email"
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="border border-white/25 bg-transparent text-white placeholder-white/40 rounded-full px-5 py-2.5 text-sm focus:outline-none focus:border-white/60 w-full"
                  />
                </div>

                {/* Phone + Date + Time */}
                <div className="flex flex-col gap-3 sm:flex-row">
                  <div className="relative w-full sm:w-1/3">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-white/60 pointer-events-none">+65</span>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="Phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="border border-white/25 bg-transparent text-white placeholder-white/40 rounded-full pl-11 pr-4 py-2.5 text-sm focus:outline-none focus:border-white/60 w-full"
                    />
                  </div>

                  <div className="relative flex-1">
                    <button
                      type="button"
                      onClick={() => { setDateOpen(!dateOpen); setTimeOpen(false); }}
                      className="w-full border border-white/25 bg-transparent rounded-full px-4 py-2.5 text-sm text-left cursor-pointer flex items-center justify-between"
                    >
                      <span className={date ? 'text-white' : 'text-white/40'}>{date || 'Choose date'}</span>
                      <i className="ri-arrow-down-s-line text-white/50" />
                    </button>
                    {dateOpen && (
                      <div className="absolute top-full mt-1 left-0 right-0 bg-[#0f1e26] border border-white/20 rounded-2xl z-20 max-h-40 overflow-y-auto">
                        {DATES.map((d) => (
                          <button key={d} type="button" onClick={() => { setDate(d); setDateOpen(false); }}
                            className="w-full text-left px-4 py-2 text-xs text-white hover:bg-white/10 cursor-pointer">
                            {d}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="relative flex-1">
                    <button
                      type="button"
                      onClick={() => { setTimeOpen(!timeOpen); setDateOpen(false); }}
                      className="w-full border border-white/25 bg-transparent rounded-full px-4 py-2.5 text-sm text-left cursor-pointer flex items-center justify-between"
                    >
                      <span className={time ? 'text-white' : 'text-white/40'}>{time || 'Choose time'}</span>
                      <i className="ri-arrow-down-s-line text-white/50" />
                    </button>
                    {timeOpen && (
                      <div className="absolute top-full mt-1 left-0 right-0 bg-[#0f1e26] border border-white/20 rounded-2xl z-20 max-h-40 overflow-y-auto">
                        {TIMES.map((t) => (
                          <button key={t} type="button" onClick={() => { setTime(t); setTimeOpen(false); }}
                            className="w-full text-left px-4 py-2 text-xs text-white hover:bg-white/10 cursor-pointer">
                            {t}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-white/50 text-xs">Showflat viewing is strictly by advance appointment only.</p>

                {/* Privacy */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <div
                    onClick={() => setPrivacy(!privacy)}
                    className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer ${
                      privacy ? 'bg-white border-white' : 'border-white/40'
                    }`}
                  >
                    {privacy && <i className="ri-check-line text-[#172C37] text-xs" />}
                  </div>
                  <input type="hidden" name="privacy" value={privacy ? 'agreed' : ''} />
                  <p className="text-white/50 text-xs leading-relaxed">
                    By submitting this form, you agree to our{' '}
                    <a href="https://book-showflat.sg/privacy-policy" target="_blank" rel="nofollow noreferrer" className="underline text-white/70">
                      Privacy Policy
                    </a>{' '}
                    and consent that we or our sales representative may contact you for the purpose of
                    your enquiry via voice call, email, SMS, WhatsApp, regardless of DNS registry status. (PDPA 2012)
                  </p>
                </label>

                {error && <p className="text-red-400 text-sm">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-white text-[#172C37] font-bold py-3 rounded-full hover:bg-white/90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : 'Enquire Now'}
                </button>
                <p className="text-white/40 text-xs text-center">
                  Please wait after clicking submit while we process your response.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-6 flex-wrap">
            <a
              href="https://book-showflat.sg/privacy-policy"
              target="_blank"
              rel="nofollow noreferrer"
              className="text-sm text-white/50 hover:text-white/80 underline transition-colors"
            >
              Privacy Policy
            </a>
            <span className="text-white/30 text-sm">Copyright &copy; 2026 Vela Bay. All Rights Reserved.</span>
          </div>

        </div>
      </div>
    </footer>
  );
}
